import type { CorePayload, Manifest, TransactionsPayload } from './types';

const SUPPORTED_SCHEMA_VERSION = 2;
const DATA_ROOT = new URL('/data/', window.location.origin);
const RECOVERY_KEY = 'money-data-recovery';

export type LoadState = 'idle' | 'loading' | 'ready' | 'error';

class RecoverableBundleError extends Error {}

function reloadOnce(bundle: 'core' | 'transactions', revision: string, error: unknown): void {
  if (!(error instanceof RecoverableBundleError)) return;
  const attempt = `${revision}:${bundle}`;
  const key = `${RECOVERY_KEY}:${bundle}`;
  if (sessionStorage.getItem(key) !== attempt) {
    sessionStorage.setItem(key, attempt);
    window.location.reload();
  }
}

async function fetchJson<T>(url: URL, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';

  if (!response.ok || !contentType.startsWith('application/json')) {
    throw new RecoverableBundleError(
      `Dati non disponibili (${response.status}, ${contentType || 'tipo sconosciuto'}).`,
    );
  }

  return response.json() as Promise<T>;
}

function assertManifest(value: Manifest): void {
  if (value.schema_version !== SUPPORTED_SCHEMA_VERSION) {
    throw new Error(
      `Versione dati ${value.schema_version} non supportata. Aggiorna l'applicazione.`,
    );
  }
  if (!value.revision || !value.bundles?.core?.url || !value.bundles?.transactions?.url) {
    throw new Error('Manifest dati incompleto.');
  }
}

export class BrowserDataLoader {
  manifestState: LoadState = 'idle';
  coreState: LoadState = 'idle';
  transactionsState: LoadState = 'idle';

  #manifestPromise: Promise<Manifest> | undefined;
  #corePromise: Promise<CorePayload> | undefined;
  #transactionsPromise: Promise<TransactionsPayload> | undefined;
  #manifest: Manifest | undefined;

  loadManifest(): Promise<Manifest> {
    if (!this.#manifestPromise) {
      this.manifestState = 'loading';
      this.#manifestPromise = fetchJson<Manifest>(new URL('manifest.json', DATA_ROOT), {
        cache: 'no-cache',
      })
        .then((manifest) => {
          assertManifest(manifest);
          this.#manifest = manifest;
          this.manifestState = 'ready';
          return manifest;
        })
        .catch((error: unknown) => {
          this.manifestState = 'error';
          this.#manifestPromise = undefined;
          throw error;
        });
    }
    return this.#manifestPromise;
  }

  loadCore(): Promise<CorePayload> {
    if (!this.#corePromise) {
      this.coreState = 'loading';
      this.#corePromise = this.loadManifest()
        .then(async (manifest) => {
          const core = await fetchJson<CorePayload>(new URL(manifest.bundles.core.url, DATA_ROOT));
          if (
            core.meta.schema_version !== manifest.schema_version ||
            core.meta.revision !== manifest.revision
          ) {
            throw new RecoverableBundleError(
              'Il core non appartiene alla revisione dichiarata dal manifest.',
            );
          }
          sessionStorage.removeItem(`${RECOVERY_KEY}:core`);
          this.coreState = 'ready';
          return core;
        })
        .catch((error: unknown) => {
          reloadOnce('core', this.#manifest?.revision ?? 'unknown', error);
          this.coreState = 'error';
          this.#corePromise = undefined;
          throw error;
        });
    }
    return this.#corePromise;
  }

  loadTransactions(): Promise<TransactionsPayload> {
    if (!this.#transactionsPromise) {
      this.transactionsState = 'loading';
      this.#transactionsPromise = Promise.all([this.loadManifest(), this.loadCore()])
        .then(async ([manifest, core]) => {
          const payload = await fetchJson<TransactionsPayload>(
            new URL(manifest.bundles.transactions.url, DATA_ROOT),
          );
          if (
            payload.meta.schema_version !== manifest.schema_version ||
            payload.meta.revision !== manifest.revision ||
            core.meta.revision !== payload.meta.revision
          ) {
            throw new RecoverableBundleError(
              'Le transazioni non appartengono alla revisione del core.',
            );
          }
          sessionStorage.removeItem(`${RECOVERY_KEY}:transactions`);
          this.transactionsState = 'ready';
          return payload;
        })
        .catch((error: unknown) => {
          reloadOnce('transactions', this.#manifest?.revision ?? 'unknown', error);
          this.transactionsState = 'error';
          this.#transactionsPromise = undefined;
          throw error;
        });
    }
    return this.#transactionsPromise;
  }

  async refreshManifest(): Promise<'unchanged' | 'changed'> {
    const currentRevision = this.#manifest?.revision;
    const next = await fetchJson<Manifest>(new URL('manifest.json', DATA_ROOT), {
      cache: 'no-cache',
    });
    assertManifest(next);
    return currentRevision === undefined || currentRevision === next.revision
      ? 'unchanged'
      : 'changed';
  }

  retryCore(): Promise<CorePayload> {
    this.#manifestPromise = undefined;
    this.#corePromise = undefined;
    return this.loadCore();
  }

  retryTransactions(): Promise<TransactionsPayload> {
    this.#transactionsPromise = undefined;
    return this.loadTransactions();
  }
}
